<template>
  <div v-if="showModal" class="price-moved-modal" @click.self="decline">
    <div class="modal-content">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <h2 class="modal-title">Price Moved</h2>
          <div class="close-btn" @click="decline">
            <img
              src="/icons/close.svg"
              alt="close"
              class="close-icon day-mode"
            />
            <img
              src="/icons/close-night.svg"
              alt="close"
              class="close-icon night-mode"
            />
          </div>
        </div>
      </div>

      <div class="body">
        <!-- Bet meta: single line (selection · game) -->
        <p class="meta-line">
          {{ data.sideString }}
          <span class="meta-line__game">· {{ data.gameInfo }}</span>
        </p>

        <!-- Asked vs Now comparison — both columns always shown -->
        <div class="compare">
          <div class="compare-row compare-head">
            <span class="cell label"></span>
            <span class="cell">Asked</span>
            <span class="cell got">Now</span>
          </div>

          <!-- Only the odds row carries red — and only when the move is
               WORSE for the bettor; the server fetch can return a better
               price and red would lie (spec-review 21) -->
          <div
            class="compare-row"
            :class="{ 'odds-changed': data.offeredWorse }"
          >
            <span class="cell label">Odds</span>
            <span class="cell">{{ data.askedOddsDisplay }}</span>
            <span class="cell got">{{ data.offeredOddsDisplay }}</span>
          </div>

          <div class="compare-row">
            <span class="cell label">Risk</span>
            <span class="cell">{{ money(data.askedRisk) }}</span>
            <span class="cell got">{{ money(data.offeredRisk) }}</span>
          </div>

          <div class="compare-row">
            <span class="cell label">To win</span>
            <span class="cell">{{ money(data.askedWin) }}</span>
            <span class="cell got">{{ money(data.offeredWin) }}</span>
          </div>
        </div>

        <p class="trim-note">
          <template v-if="trimmed">
            Trimmed from {{ money(data.askedRisk) }}. Only
            {{ money(data.available) }} available at
            {{ data.offeredOddsDisplay }}.
          </template>
          <template v-else>
            Books at {{ data.offeredOddsDisplay }} or better only.
          </template>
        </p>
      </div>

      <!-- Action Buttons -->
      <div class="modal-actions">
        <button class="continue-btn" @click="confirm">
          Book {{ money(data.offeredRisk) }} at {{ data.offeredOddsDisplay }}
        </button>
        <button class="cancel-btn" @click="decline">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const showModal = ref(false);
const data = ref({
  gameInfo: '',
  sideString: '',
  askedOddsDisplay: '',
  offeredOddsDisplay: '',
  askedRisk: 0,
  offeredRisk: 0,
  askedWin: 0,
  offeredWin: 0,
  available: 0,
  offeredWorse: true,
  onConfirm: null,
  onCancel: null,
});

const trimmed = computed(
  () => Number(data.value.offeredRisk) < Number(data.value.askedRisk),
);

function money(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '$0.00';
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function show(priceMovedData) {
  data.value = {
    gameInfo: priceMovedData?.gameInfo ?? '',
    sideString: priceMovedData?.sideString ?? '',
    askedOddsDisplay: priceMovedData?.askedOddsDisplay ?? '',
    offeredOddsDisplay: priceMovedData?.offeredOddsDisplay ?? '',
    askedRisk: priceMovedData?.askedRisk ?? 0,
    offeredRisk: priceMovedData?.offeredRisk ?? 0,
    askedWin: priceMovedData?.askedWin ?? 0,
    offeredWin: priceMovedData?.offeredWin ?? 0,
    available: priceMovedData?.available ?? 0,
    offeredWorse: priceMovedData?.offeredWorse ?? true,
    onConfirm: priceMovedData?.onConfirm ?? null,
    onCancel: priceMovedData?.onCancel ?? null,
  };
  showModal.value = true;
  // Ack to the awaiting take flow that the prompt is actually on screen —
  // its no-listener timeout stands down only on this signal (bugbot).
  priceMovedData?.onShown?.();
}

function confirm() {
  showModal.value = false;
  data.value.onConfirm?.();
}

function decline() {
  showModal.value = false;
  data.value.onCancel?.();
}

// Programmatic close for when the owning slip unmounts mid-prompt
// (spec-review 20 M5): settles the awaiting flow as an auto-decline so it
// can't hang, and never books from a dead slip.
function dismiss() {
  if (!showModal.value) return;
  showModal.value = false;
  data.value.onCancel?.('slip_closed');
}

defineExpose({
  show,
  dismiss,
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

.price-moved-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: main.$black-202;
  display: flex;
  align-items: center;
  justify-content: center;
  // Must stack ABOVE the still-open take bet modal (unlike FillSummaryModal,
  // which only ever renders after it closes). 1100 is the same tier as
  // SlippageConfirmModal, the existing prompt-over-modal.
  z-index: 1100;
  padding: 1rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    padding: 0.5rem;
  }
}

.modal-content {
  background: main.$white_01;
  border-radius: 12px;
  box-shadow: 0 4px 20px main.$black-201;
  width: 100%;
  max-width: 450px;
  overflow: hidden;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    max-width: 100%;
    border-radius: 8px;
  }
}

.modal-header {
  background: main.$color-primary_02;
  padding: 1rem 1.5rem;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-title {
    color: main.$white;
    font-size: 1.25rem;
    font-weight: main.$font-weight-bold;
    margin: 0;
    font-family: 'Poppins Bold', Arial, sans-serif;
  }

  .close-btn {
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    transition: background-color 0.2s ease;

    &:hover {
      background: main.$black-103;
    }

    .close-icon {
      width: 20px;
      height: 20px;
    }
  }
}

.body {
  padding: 1.5rem;
  background: main.$white;
}

// A visible-but-quiet divider. The real token ($grey-402 = #f6f6f6) is nearly
// invisible, which made the table float; this reads as a table without
// shouting (Lucas + Morley feedback).
$divider: #e7e8ef;
// Softer ink than pure black for the numbers, and a softer red than the
// stock $red-300 (#f70000) for the one price that moved.
$ink: #1f2437;

.meta-line {
  margin: 0 0 0.9rem;
  color: $ink;
  font-size: 0.8125rem;
  font-weight: main.$font-weight-bold;
  font-family: 'Poppins Bold', Arial, sans-serif;

  .meta-line__game {
    color: main.$black-203;
    font-weight: main.$font-weight-semibold;
    font-family: 'Poppins Medium', Arial, sans-serif;
  }
}

.compare {
  border: 1px solid $divider;
  border-radius: 8px;
  overflow: hidden;

  .compare-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid $divider;

    &:last-child {
      border-bottom: none;
    }

    .cell {
      font-size: 0.875rem;
      text-align: right;
      color: $ink;
      font-weight: main.$font-weight-semibold;
      font-family: 'Poppins Medium', Arial, sans-serif;

      &.label {
        text-align: left;
        color: main.$black-203;
      }

      // NOW column defaults to neutral dark — only the odds row goes red.
      &.got {
        color: $ink;
      }
    }
  }

  .compare-head {
    background: main.$white-200;

    .cell {
      font-size: 0.75rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: main.$black-203;
      font-family: 'Poppins Medium', Arial, sans-serif;

      &.got {
        color: main.$color-primary;
      }
    }
  }

  // Red marks ONLY the price that actually moved; the trimmed risk/to-win
  // stay neutral so the whole table doesn't read as an alarm (Lucas).
  .compare-row.odds-changed {
    .cell.got {
      color: main.$red-301;
      font-weight: main.$font-weight-bold;
      font-family: 'Poppins Bold', Arial, sans-serif;
    }
  }
}

.trim-note {
  margin: 0.75rem 0 0;
  color: main.$black-203;
  font-size: 0.75rem;
  line-height: 1.4;
  font-family: 'Poppins Medium', Arial, sans-serif;
}

.modal-actions {
  padding: 1.5rem;
  background: main.$white_01;
  display: flex;
  justify-content: center;
  gap: 0.75rem;

  .continue-btn {
    background: main.$color-primary;
    color: main.$white;
    border: none;
    border-radius: 8px;
    padding: 0.875rem 1.5rem;
    font-size: 0.9375rem;
    font-weight: main.$font-weight-bold;
    font-family: 'Poppins Bold', Arial, sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 120px;

    &:hover {
      background: main.$color-primary_04;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px main.$black-103;
    }

    &:active {
      transform: translateY(0);
    }

    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      padding: 1rem 1rem;
      font-size: 0.875rem;
    }
  }

  .cancel-btn {
    background: main.$blue-901_03;
    color: main.$blue-903_01;
    border: none;
    border-radius: 8px;
    padding: 0.875rem 1.5rem;
    font-size: 0.9375rem;
    font-weight: main.$font-weight-bold;
    font-family: 'Poppins Bold', Arial, sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 100px;

    &:hover {
      filter: brightness(0.95);
    }
  }
}

// Night mode
[theme='night'] {
  .modal-content {
    background: main.$white_01;
  }

  .body {
    background: main.$white_01;
  }

  // Full table night overrides (spec-review 21 M15: the clone dropped the
  // night rules its template source carries — day-only header background
  // with near-white text, and the day ink/dividers don't survive a dark
  // body). $black / $red-301 are theme vars and flip to light values.
  .meta-line {
    color: main.$black;

    .meta-line__game {
      color: main.$black-203_01;
    }
  }

  .compare {
    border-color: rgba(255, 255, 255, 0.12);

    .compare-row {
      border-bottom-color: rgba(255, 255, 255, 0.12);

      .cell {
        color: main.$black;

        &.label {
          color: main.$black-203_01;
        }

        &.got {
          color: main.$black;
        }
      }
    }

    .compare-head {
      background: rgba(255, 255, 255, 0.06);

      .cell {
        color: main.$black-203_01;

        &.got {
          color: main.$color-primary_01;
        }
      }
    }

    .compare-row.odds-changed {
      .cell.got {
        color: main.$red-301;
      }
    }
  }

  .trim-note {
    color: main.$black-203_01;
  }

  .day-mode {
    display: none;
  }
}

[theme='day'] {
  .night-mode {
    display: none;
  }
}
</style>
